import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FAILURE_SEGMENTS,
  PLATFORM_MONITOR_COMPONENTS,
  STATUS_LABEL,
  STATUS_TONE,
  type AdminSurfaceStatus,
  type FailureSegment,
  type PlatformMonitorComponent,
} from '../../platform/adminPlatformModel';
import { formatRelativeUs, formatTimestampUs } from '../../lib/format';
import { getAdminStats, getMonitoringComponents, getVersionInfo, type AdminStats, type MonitorComponent, type VersionInfo } from '../_shared/adminTelemetryApi';
import { getPlatformSettings, type PlatformSettings } from '../platform/platformApi';
import '../_shared/adminObservability.css';
import './monitoring.css';

type LoadState<T> = T | null | 'error';

interface MonitoringProbes {
  version: LoadState<VersionInfo>;
  stats: LoadState<AdminStats>;
  settings: LoadState<PlatformSettings>;
}

function StatusPill({ status }: { status: AdminSurfaceStatus }) {
  return <span className={`pill pill-dot ${STATUS_TONE[status]}`}>{STATUS_LABEL[status]}</span>;
}

function EntryLink({ route }: { route?: string }) {
  if (!route) return <span className="text-tertiary">外部系統</span>;
  return (
    <Link className="btn-ghost btn-sm" to={route}>
      開啟
    </Link>
  );
}

// 開發者除錯用端點徽章:平常只留一個資訊符號,hover/focus 才看完整端點字串
// (比照 reports 的 DevHint,不把技術端點字串常駐在標題列)。
function DevHint({ endpoint }: { endpoint: string }) {
  return (
    <span className="mon-dev-hint" title={endpoint} aria-label={`資料端點 ${endpoint}`}>
      ⓘ
    </span>
  );
}

function probeText<T>(value: LoadState<T>, render: (data: T) => string): string {
  if (value === null) return '…';
  if (value === 'error') return '—';
  return render(value);
}

// probe 狀態色調:探測中=中性、正常=teal、失敗=危險色,對應左側狀態色條。
function probeTone(value: LoadState<unknown>): 'loading' | 'ok' | 'error' {
  if (value === null) return 'loading';
  if (value === 'error') return 'error';
  return 'ok';
}

// nonce 遞增即重探(輪詢 / 手動刷新);重探期間保留上一輪值,避免每 20s 閃爍。
function useMonitoringProbes(nonce: number): MonitoringProbes {
  const [version, setVersion] = useState<LoadState<VersionInfo>>(null);
  const [stats, setStats] = useState<LoadState<AdminStats>>(null);
  const [settings, setSettings] = useState<LoadState<PlatformSettings>>(null);

  useEffect(() => {
    const controller = new AbortController();
    let alive = true;

    getVersionInfo(controller.signal)
      .then((result) => alive && setVersion(result.data ?? 'error'))
      .catch(() => alive && setVersion('error'));
    getAdminStats(controller.signal)
      .then((result) => alive && setStats(result.data ?? 'error'))
      .catch(() => alive && setStats('error'));
    getPlatformSettings(controller.signal)
      .then((result) => alive && setSettings(result.data ?? 'error'))
      .catch(() => alive && setSettings('error'));

    return () => {
      alive = false;
      controller.abort();
    };
  }, [nonce]);

  return { version, stats, settings };
}

// R18 即時健康(restful!151):端點未部署 → 'unavailable',保留宣告式矩陣。
type LiveState = MonitorComponent[] | null | 'unavailable';

function useLiveComponents(nonce: number, onSettled?: () => void): LiveState {
  const [live, setLive] = useState<LiveState>(null);
  useEffect(() => {
    const controller = new AbortController();
    let alive = true;
    getMonitoringComponents(controller.signal)
      .then((r) => alive && setLive(Array.isArray(r.data?.components) ? r.data.components : 'unavailable'))
      .catch(() => alive && setLive('unavailable'))
      .finally(() => alive && onSettled?.());
    return () => {
      alive = false;
      controller.abort();
    };
    // onSettled 每 render 都是新 fn,但只在 nonce 變時重探;故意不列入 deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nonce]);
  return live;
}

const LIVE_STATUS_PILL: Record<string, string> = {
  up: 'pill-good',
  degraded: 'pill-warn',
  down: 'pill-danger',
  unknown: 'pill-neutral',
};

// 即時健康狀態中文化(顯示轉譯,送 API/比較的原始 enum 值不變)。
const LIVE_STATUS_LABEL: Record<string, string> = {
  up: '正常',
  degraded: '降級',
  down: '中斷',
  unknown: '未知',
};

// 平台設定驗證狀態中文化(純顯示;'…' 探測中 / '—' 失敗等非 enum 值原樣通過)。
const VALIDATION_STATE_LABEL: Record<string, string> = {
  ok: '正常',
  warning: '警告',
  error: '錯誤',
  unknown: '未知',
};

function ProbeCard({
  label,
  value,
  foot,
  tone,
  hero,
}: {
  label: string;
  value: string;
  foot: string;
  tone: 'loading' | 'ok' | 'error';
  hero?: boolean;
}) {
  return (
    <div className={`obs-probe obs-probe--tone-${tone}${hero ? ' obs-probe--hero' : ''}`}>
      <span className="obs-probe-label">{label}</span>
      <span className="obs-probe-value">{value}</span>
      <span className="obs-probe-foot">{foot}</span>
    </div>
  );
}

// Runbook 兩段式呈現:表格內只放摘要一行(截斷+tooltip),展開後在該列下方插入全寬說明列。
function ComponentRow({ component }: { component: PlatformMonitorComponent }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <tr data-testid={`component-${component.id}`}>
        <td className="col-component">
          <div className="obs-meta-stack">
            <span className="obs-title col-component-text" title={component.component}>
              {component.component}
            </span>
            <span className="text-tertiary">{component.owner}</span>
          </div>
        </td>
        <td className="col-status">
          <StatusPill status={component.status} />
        </td>
        <td>{component.liveSignal}</td>
        <td className="mono obs-source">{component.monitoringSource}</td>
        <td className="mono col-segment" title={component.failureSegment}>
          {component.failureSegment}
        </td>
        <td className="col-runbook">
          <div className="obs-runbook-summary">
            <span className="obs-runbook-summary-text" title={component.runbook}>
              {component.runbook}
            </span>
            <button
              type="button"
              className="obs-runbook-toggle"
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
              data-testid={`runbook-toggle-${component.id}`}
            >
              {expanded ? '收合' : '展開'}
            </button>
          </div>
        </td>
        <td className="obs-entry">
          <EntryLink route={component.route} />
        </td>
      </tr>
      {expanded && (
        <tr className="obs-runbook-expand" data-testid={`runbook-detail-${component.id}`}>
          <td colSpan={7}>
            <span className="text-tertiary">維運手冊：</span> {component.runbook}
          </td>
        </tr>
      )}
    </>
  );
}

function FailureSegmentCard({ segment }: { segment: FailureSegment }) {
  return (
    <article className="obs-segment-card" data-testid={`failure-${segment.id}`}>
      <span className="obs-card-kicker mono">{segment.id}</span>
      <h3>{segment.label}</h3>
      <p className="obs-card-body">{segment.when}</p>
      <p className="obs-card-body">
        <span className="text-tertiary">負責:</span> {segment.owner}
      </p>
      <p className="obs-card-body">
        <span className="text-tertiary">處置:</span> {segment.action}
      </p>
    </article>
  );
}

const REFRESH_MS = 20000; // 即時健康輪詢週期

export default function MonitoringPage() {
  const [nonce, setNonce] = useState(0);
  const [lastUpdated, setLastUpdated] = useState<number | null>(null);
  // 「即時」名副其實:每 20s 自動重探 + 手動刷新;卸載清 timer
  useEffect(() => {
    const id = setInterval(() => setNonce((n) => n + 1), REFRESH_MS);
    return () => clearInterval(id);
  }, []);
  const probes = useMonitoringProbes(nonce);
  const liveComponents = useLiveComponents(nonce, () => setLastUpdated(Date.now()));
  const settingsData = probes.settings && probes.settings !== 'error' ? probes.settings : null;

  const secretSummary = useMemo(() => {
    if (probes.settings === null) return '…';
    if (probes.settings === 'error' || !settingsData) return '—';
    const values = Object.values(settingsData.secrets ?? {});
    const configured = values.filter((value) => value === 'configured' || value === 'not-managed-by-rest').length;
    const missing = values.filter((value) => value === 'missing').length;
    return `${configured} 組態完成 / ${missing} 缺失`;
  }, [probes.settings, settingsData]);

  const validationStateRaw = probeText(probes.settings, (settings) => settings.status?.validation_state ?? settings.validation?.validation_state ?? 'unknown');
  const validationState = VALIDATION_STATE_LABEL[validationStateRaw] ?? validationStateRaw;

  return (
    <div className="obs-page monitoring-page" data-testid="monitoring-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">平台監控</h1>
          <p className="page-subtitle">平台各元件的健康狀態、外部監控來源與故障歸因說明。</p>
        </div>
        <div className="obs-refresh-bar">
          <span className="text-tertiary" data-testid="monitoring-last-updated">
            {lastUpdated ? `上次更新 ${new Date(lastUpdated).toLocaleTimeString('zh-TW', { hour12: false })}` : '尚未更新'}
            {`・每 ${REFRESH_MS / 1000}s 自動`}
          </span>
          <button className="btn-ghost btn-sm" onClick={() => setNonce((n) => n + 1)}>
            ↻ 立即重探
          </button>
        </div>
      </div>

      <div className="obs-probe-grid" data-testid="monitoring-live-probes" aria-label="平台即時探測">
        <ProbeCard
          label="REST API 服務"
          value={probeText(probes.version, (version) => version.version ?? '未知')}
          foot={probeText(probes.version, (version) => `${version.service ?? 'restful-api'}${version.commit ? ` / ${version.commit.slice(0, 8)}` : ''}`)}
          tone={probeTone(probes.version)}
          hero
        />
        <ProbeCard
          label="平台設定"
          value={validationState}
          foot={settingsData?.source ?? '平台啟動設定快照'}
          tone={probeTone(probes.settings)}
        />
        <ProbeCard
          label="後台統計"
          value={probeText(probes.stats, (stats) => `${stats.devices ?? 0} 台裝置`)}
          foot={probeText(probes.stats, (stats) => `${stats.events ?? 0} 筆事件 / ${stats.ota_jobs ?? 0} 筆 OTA 工作`)}
          tone={probeTone(probes.stats)}
        />
        <ProbeCard label="機密狀態" value={secretSummary} foot="僅顯示已消毒狀態" tone={probeTone(probes.settings)} />
      </div>

      <section className="obs-section" data-testid="monitoring-live-health">
        <div className="section-head">
          <h2 className="section-title">
            <span className="obs-live-dot" aria-hidden="true" />
            即時健康
          </h2>
          <span className="section-meta">
            R18 即時探針
            <DevHint endpoint="GET /v3/admin/monitoring/components(R18 v1)" />
          </span>
        </div>
        {liveComponents === null && <div className="inline-notice">正在探測即時健康資料…</div>}
        {liveComponents === 'unavailable' && (
          <div className="inline-notice inline-notice--info">
            <span>
              監控探針尚未上線,下方為既定就緒度矩陣,端點部署後將自動顯示即時資料。
              <br />
              <span className="obs-endpoint-note">(restful!151,GET /v3/admin/monitoring/components)</span>
            </span>
          </div>
        )}
        {Array.isArray(liveComponents) && liveComponents.length === 0 && (
          <div className="inline-notice inline-notice--warn">端點已部署但尚無探測對象,請確認 RA_MONITOR_TARGETS 設定。</div>
        )}
        {Array.isArray(liveComponents) && liveComponents.length > 0 && (
          <div className="table-scroll obs-table-wrap mon-live-scroll">
            <table className="data-table obs-table">
              <thead>
                <tr>
                  <th>元件</th>
                  <th>狀態</th>
                  <th>HTTP 狀態碼</th>
                  <th>訊號來源</th>
                  <th>檢查時間</th>
                </tr>
              </thead>
              <tbody>
                {liveComponents.map((c) => (
                  <tr key={c.component}>
                    <td className="obs-title">{c.component}</td>
                    <td>
                      <span className={`pill pill-dot ${LIVE_STATUS_PILL[c.status] ?? 'pill-neutral'}`}>
                        {LIVE_STATUS_LABEL[c.status] ?? c.status}
                      </span>
                    </td>
                    <td className="mono">{c.http_status ?? '—'}</td>
                    <td className="mono obs-source">{c.signal_source ?? '—'}</td>
                    <td className="mono" title={formatTimestampUs(c.checked_at)}>{formatRelativeUs(c.checked_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="obs-section">
        <div className="section-head">
          <h2 className="section-title">元件矩陣</h2>
          <span className="section-meta">監控涵蓋就緒度(建置狀態,非即時健康 —— 以上方「即時健康」為當下真相)</span>
        </div>
        <div className="table-scroll obs-table-wrap" data-testid="monitoring-components">
          <table className="data-table obs-table obs-table--matrix">
            <thead>
              <tr>
                <th className="col-component">元件</th>
                <th className="col-status">就緒度</th>
                <th>即時訊號</th>
                <th>監控來源</th>
                <th className="col-segment">故障段</th>
                <th className="col-runbook">維運手冊</th>
                <th aria-label="入口" />
              </tr>
            </thead>
            <tbody>
              {PLATFORM_MONITOR_COMPONENTS.map((component) => (
                <ComponentRow key={component.id} component={component} />
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="obs-section">
        <div className="section-head">
          <h2 className="section-title">故障歸因</h2>
          <span className="section-meta">先定位是哪一層出問題,不一定是程式碼</span>
        </div>
        <div className="obs-segment-grid" data-testid="failure-segments">
          {FAILURE_SEGMENTS.map((segment) => (
            <FailureSegmentCard key={segment.id} segment={segment} />
          ))}
        </div>
      </section>
    </div>
  );
}
